import { HandoffResult } from './types'

// Must match the extension's HANDOFF_MESSAGE_TYPE
// (quickpass-extension/_configuration/configuration.ts).
const HANDOFF_MESSAGE_TYPE = 'quickpass-extension-auth'

interface ExternalRuntime {
  sendMessage: (
    extensionId: string,
    message: object,
    callback: (response: unknown) => void,
  ) => void
  lastError?: { message?: string }
}

interface ChromeGlobal {
  runtime?: ExternalRuntime
}

interface HandoffPayload {
  type: string
  refreshToken: string
  uid: string
  email: string
}

/**
 * Reads the extension's runtime ID from the query string. The extension passes
 * it as `?ext=<id>` (before the hash) when it opens this page, so it can be
 * read directly off `location.search` regardless of the hash router.
 *
 * @returns {string | null} The extension ID, or null when absent.
 */
const readExtensionId = (): string | null => {
  const value = new URLSearchParams(window.location.search).get('ext')

  if (value === null || value.trim() === '') {
    return null
  }

  return value.trim()
}

/**
 * Resolves the `chrome.runtime` messaging surface injected into this page by
 * the extension's `externally_connectable` declaration. Returns null when the
 * extension is not installed (or this is a non-Chromium browser).
 *
 * @returns {ExternalRuntime | null} The runtime messenger, or null.
 */
const getExtensionRuntime = (): ExternalRuntime | null => {
  const chromeGlobal = (window as unknown as { chrome?: ChromeGlobal }).chrome
  return chromeGlobal?.runtime ?? null
}

/**
 * Narrows the extension's acknowledgement response.
 *
 * @param {unknown} response - The raw callback response.
 * @returns {boolean} True when the extension reported success.
 */
const isSuccessAcknowledgement = (response: unknown): boolean => {
  return (
    typeof response === 'object' &&
    response !== null &&
    (response as Record<string, unknown>).success === true
  )
}

/**
 * Sends the signed-in session to the extension over the externally-connectable
 * channel and resolves with the extension's acknowledgement.
 *
 * @param {string} extensionId - The target extension's runtime ID.
 * @param {string} refreshToken - The Firebase refresh token to hand off.
 * @param {string} uid - The renter's Firebase Auth UID.
 * @param {string} email - The renter's email.
 * @returns {Promise<HandoffResult>} Whether the handoff was acknowledged.
 */
const sendHandoffToExtension = (
  extensionId: string,
  refreshToken: string,
  uid: string,
  email: string,
): Promise<HandoffResult> => {
  const runtime = getExtensionRuntime()

  if (runtime === null) {
    return Promise.resolve({ ok: false, error: 'no-extension' })
  }

  const payload: HandoffPayload = {
    type: HANDOFF_MESSAGE_TYPE,
    refreshToken,
    uid,
    email,
  }

  return new Promise<HandoffResult>(resolve => {
    runtime.sendMessage(extensionId, payload, (response: unknown) => {
      const lastErrorMessage = runtime.lastError?.message

      if (typeof lastErrorMessage === 'string') {
        resolve({ ok: false, error: lastErrorMessage })
        return
      }

      if (!isSuccessAcknowledgement(response)) {
        resolve({ ok: false, error: 'The extension could not be connected.' })
        return
      }

      resolve({ ok: true })
    })
  })
}

export { readExtensionId, getExtensionRuntime, sendHandoffToExtension }
